import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

import { DEFAULT_LANG, LANGS, type I18nBundle, type Lang } from './types';

const isSupported = (value: string): value is Lang =>
  (LANGS as readonly string[]).includes(value);

const resolveInitialLanguage = (): Lang => {
  if (typeof window === 'undefined') return DEFAULT_LANG;

  try {
    const stored = window.localStorage.getItem('language');
    if (stored) {
      let candidate: string = stored;
      try {
        const parsed = JSON.parse(stored);
        if (typeof parsed === 'string') candidate = parsed;
      } catch {
        // value was stored raw, not JSON — keep as-is
      }
      if (isSupported(candidate)) return candidate;
    }
  } catch {
    // localStorage access denied (private mode / sandbox)
  }

  const browserLang = window.navigator?.language ?? '';
  if (browserLang.startsWith('es')) return 'es';
  if (browserLang.startsWith('pt')) return 'pt';
  if (browserLang.startsWith('en')) return 'en';
  return DEFAULT_LANG;
};

export interface CreateI18nOptions {
  /** Injected as `{{brand}}` into every translation. */
  brandName: string;
  /** Core bundle first, then one bundle per mounted feature module. */
  bundles: I18nBundle[];
}

/**
 * Initializes the shared i18next instance with exactly the namespaces the
 * current dashboard ships. Call once at bootstrap, before rendering.
 */
export function createI18n({ brandName, bundles }: CreateI18nOptions) {
  const resources: Record<Lang, Record<string, object>> = { es: {}, en: {}, pt: {} };
  for (const bundle of bundles) {
    for (const lang of LANGS) {
      Object.assign(resources[lang], bundle[lang]);
    }
  }

  void i18n.use(initReactI18next).init({
    resources,
    lng: resolveInitialLanguage(),
    fallbackLng: DEFAULT_LANG,
    ns: Object.keys(resources[DEFAULT_LANG]),
    defaultNS: 'global',
    interpolation: {
      escapeValue: false,
      defaultVariables: { brand: brandName },
    },
  });

  return i18n;
}
